/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-lines */
import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { deleteFilter, makeDefaultFilter } from 'api/Filter';

import { cn, ia, iaRa } from 'lib/helpers/utility';
import { useFilters } from 'lib/hooks/queries/filters/useFilters';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import useModal from 'lib/hooks/useModal';

import Button from 'components/shared/Buttons/Button/Button';
import { applyFilterHandler } from 'components/shared/Filter/lib/helpers/handleAppliedFilter';
import SaveFilterModal from 'components/shared/Filters/components/SaveFilterModal';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';

const DropdownTabs = ({
  defaultActiveItem,
  items,
  category,
  filters,
  setFilters,
  defaultFilters,
  setInitialFilters,
  setIsEditingFilter,
  removeDefaults = false
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status');
  const filter = searchParams.get('view');

  const navigate = useNavigate();

  const { isOpen, closeModal, openModal } = useModal();
  const { isOpen: isEditOpen, closeModal: closeEditModal, openModal: openEditModal } = useModal();

  const onSwitchItemClick = (item) => {
    if (item.navigate) {
      navigate(item.navigate);
      return;
    }
    setIsEditingFilter(false);
    setSearchParams((prev) => {
      prev.delete('view');
      if (item.value === null) {
        prev.delete('status');
        setFilters({});
        setInitialFilters({});
      } else {
        prev.set('status', item.value);
        setFilters({});
        setInitialFilters({});
      }
      return prev;
    });
  };

  const { data: filtersData } = useFilters({
    params: { category },
    dependencies: [category]
  });

  useEffect(() => {
    if (filtersData?.default?.filters && !status) {
      setSearchParams((prev) => {
        if (!filtersData?.default?.filters) return prev;
        prev.set('view', filtersData?.default?.name);
        return prev;
      });
      setFilters(applyFilterHandler(defaultFilters, filtersData?.default));
      setInitialFilters(applyFilterHandler(defaultFilters, filtersData?.default));
      setIsEditingFilter(true);
    }
  }, [filtersData?.default]);

  const onFilterClick = (filter, isEditing = false) => {
    setIsEditingFilter(isEditing);
    setSearchParams((prev) => {
      prev.delete('status');
      if (filter.name === null) {
        prev.delete('view');
        setFilters({});
        setInitialFilters({});

        return prev;
      }
      const selectedFilter = iaRa(filtersData?.filters).find((f) => f.name === filter.name);

      if (!selectedFilter) return prev;

      prev.set('view', filter.name);
      const appliedFilters = applyFilterHandler(defaultFilters, selectedFilter);
      setFilters(appliedFilters);
      setInitialFilters(appliedFilters);
      return prev;
    });
  };

  const { mutate: deleteFilterHandler } = useCustomMutation({
    mutationFn: (params) => deleteFilter(navigate, params),
    successMessage: 'Filter deleted successfully',
    errorMessage: 'Failed to delete filter. Please try again later.',
    invalidateQueryKey: ['default-filters', ['filters', category]],
    onSuccess: () => {
      setFilters({});
      setSearchParams((prev) => {
        const view = prev.get('view');
        if (view === isOpen.name) {
          prev.delete('view');
        }
        return prev;
      });
      closeModal();
    }
  });
  const { mutate: makeDefaultFilterHandler } = useCustomMutation({
    mutationFn: (params) => makeDefaultFilter(navigate, params),
    successMessage: 'Filter set as default successfully',
    errorMessage: 'Failed to set filter as default. Please try again later.',
    invalidateQueryKey: ['default-filters', ['filters', category]]
  });

  const handleDeleteFilter = () => deleteFilterHandler({ id: isOpen.id });

  const removeItems = removeDefaults && !ia(filtersData?.filters) ? [] : items;

  const toggleItems = useMemo(() => {
    let firstActionAdded = false;
    return iaRa(removeItems)
      .map((item) => ({
        ...item,
        onClick: () => onSwitchItemClick(item)
      }))
      .concat(
        iaRa(filtersData?.filters).map((filter) => {
          const item = {
            label: filter.name,
            value: filter.name,
            className: '!px-2 py-1 hover:bg-primary-50',
            default: filter.is_default,
            onClick: () => onFilterClick(filter, true),
            actions: [
              {
                label: 'Rename View',
                value: 'edit',
                icon: 'editor-firstline3rec-mono',
                onClick: () => openEditModal(filter),
                textClassName: 'text-neutral-900'
              },
              {
                label: filter.is_default ? 'Unset as default view' : 'Set as default view',
                value: 'edit',
                icon: 'star-1-mono',
                onClick: () =>
                  makeDefaultFilterHandler({
                    id: filter.id,
                    category,
                    isDefault: !filter.is_default
                  }),
                textClassName: 'text-neutral-900'
              },
              {
                label: 'Delete',
                value: 'delete',
                icon: 'trash-mono',
                onClick: () => openModal({ id: filter.id, name: filter.name }),
                textClassName: 'text-neutral-900'
              }
            ]
          };
          if (!firstActionAdded && item.actions) {
            item.split = true;
            firstActionAdded = true;
          }
          return item;
        })
      )
      .map((item, index) => ({
        ...item,
        tooltip: `${item.label} - CTRL + ${index + 1}`
      }));
  }, [filtersData, items, category, removeItems]);

  useEffect(() => {
    let buffer = '';

    const handleKeyDown = (e) => {
      if (e.ctrlKey && /^\d$/.test(e.key)) {
        buffer += e.key;

        const index = parseInt(buffer, 10) - 1;
        if (!isNaN(index) && index < toggleItems.length) {
          const selectedItem = toggleItems[index];

          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);

            const isFromItems = index < iaRa(items).length;

            if (selectedItem?.value === null) {
              newParams.delete(isFromItems ? 'status' : 'view');
            } else {
              newParams.set(isFromItems ? 'status' : 'view', selectedItem.value);
              newParams.delete(isFromItems ? 'view' : 'status');
            }

            return newParams;
          });
        }
      }
    };

    const handleKeyUp = (e) => {
      if (!e.ctrlKey || !/^\d$/.test(e.key)) {
        buffer = '';
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [setSearchParams, toggleItems, items]);

  const findItems = toggleItems.map((item) => ({
    ...item,
    textClassName: 'text-neutral-900',
    ...(item.actions && {
      component: ({ handleClose }) => {
        return (
          <Popover
            isFixed
            options={item.actions}
            placement="bottom-end"
            buttonClassName="border-none min-w-6 min-h-6 w-6 h-6"
            icon="context-menu-horizontal"
            position="left"
            onOptionClick={handleClose}
            iconColor="neutral"
            iconSize={16}
            iconsShade={700}
            textClassName="text-neutral-900"
            optionIconColor="neutral"
            menuPortalTarget={document.body}
          />
        );
      }
    })
  }));

  return (
    <>
      <div className="flex w-full gap-2">
        {ia(findItems) && (
          <Popover
            isFixed
            options={findItems}
            isDropdown
            optionIconSize={16}
            optionIconColor="neutral"
            iconRightColor="neutral-800"
            iconsShade={600}
            menuPortalTarget={document.body}
            panelClassName="overflow-auto max-h-[500px]"
            customButtonTrigger={
              <Button
                variant="tertiary"
                size="xs"
                text={filter || defaultActiveItem}
                iconRight="chevron-down-linear"
                iconColor="neutral"
                textClassName="whitespace-nowrap"
                className={cn(filter && 'bg-neutral-100')}
              />
            }
          />
        )}
      </div>

      {isOpen && (
        <Confirm
          isOpen={isOpen}
          variant="danger"
          handleClose={closeModal}
          handleContinue={handleDeleteFilter}
          title={`Delete view "${isOpen.name}"`}
          icon="trash-mono"
          message="You cannot do undo this action"
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}
      {isEditOpen && (
        <SaveFilterModal
          title="Rename View"
          isOpen={isEditOpen}
          currentView={isEditOpen}
          onCancel={closeEditModal}
          filters={filters}
          category={category}
          currentViews={filtersData?.filters}
        />
      )}
    </>
  );
};

export default DropdownTabs;
